import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageProperties } from '../types'
import { pb, getCustomerLease, preparePaymentMethodUpdate, recordPaymentMethodUpdate, recordPaymentMethodFailure } from '../api-utils'
import { Customer } from '../site/pbTypes'
import { separator } from '../App'
import { useSessionExpiredModal } from '../contexts/SessionExpiredModalContext'
import { appendHelcimPayIframe, removeHelcimPayIframe, setupPaymentEventListener } from '../payment-utils'
import './ItemList.css'

type PaymentMethodModalState = 'closed' | 'consent' | 'updating' | 'success' | 'error'

// HelcimPay.js's ABORTED eventMessage is a raw string like:
//   HelcimPay.js transaction aborted – "Transaction declined. DECLINED"
// Strip that developer-facing preamble and map the underlying reason to
// clear, actionable copy. This is a self-service flow — never tell the
// customer to call, since the whole point is they shouldn't have to.
function friendlyPaymentMethodError(eventMessage: unknown): string {
    const raw = typeof eventMessage === 'string' ? eventMessage : ''
    const reason = raw.replace(/^.*[–-]\s*/, '').replace(/^"|"$/g, '').trim()

    if (/expired/i.test(reason)) return 'That card has expired. Please try a different card.'
    if (/insufficient/i.test(reason)) return 'That card was declined for insufficient funds. Please try a different card.'
    if (/cvv|security code/i.test(reason)) return 'The security code (CVV) entered was incorrect. Please check it and try again.'
    if (/invalid.*(card|number)|card.*invalid/i.test(reason)) return 'That card number looks invalid. Please double-check it and try again.'
    if (/declined/i.test(reason)) return 'That card was declined. Please double-check your card details or try a different card.'
    return reason || 'That card could not be processed. Please double-check your card details or try a different card.'
}

const CustomerDashboard = ({ page }: PageProperties) => {
    const navigate = useNavigate()
    const { setIsSessionExpiredModalOpen } = useSessionExpiredModal()
    const [customer, setCustomer] = useState<Customer | null>(null)
    const [leaseFileUrl, setLeaseFileUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [showNoLeaseModal, setShowNoLeaseModal] = useState(false)
    const [pmModalState, setPmModalState] = useState<PaymentMethodModalState>('closed')
    const [pmConsentChecked, setPmConsentChecked] = useState(false)
    const [pmError, setPmError] = useState<string>('')
    const [pmLast4, setPmLast4] = useState<string>('')

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (pb.authStore.record?.id) {
                    const [customerData, leaseResponse] = await Promise.all([
                        pb.collection('customers').getOne<Customer>(
                            pb.authStore.record.id,
                            { $autoCancel: false }
                        ),
                        getCustomerLease(pb.authStore.record.id)
                    ])
                    setCustomer(customerData)
                    const lease = leaseResponse?.lease
                    if (lease?.signed_lease) {
                        const base = pb.getFileUrl(lease, lease.signed_lease)
                        const token = pb.authStore.token
                        setLeaseFileUrl(token ? `${base}?token=${encodeURIComponent(token)}` : base)
                    }
                }
            } catch (error: any) {
                console.error('Error fetching customer data:', error)
                if (error?.status === 401 || error?.status === 403) {
                    pb.authStore.clear()
                    setIsSessionExpiredModalOpen(true)
                }
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [])

    const handleLogout = () => {
        pb.authStore.clear()
        navigate('/')
    }

    const handleStartPaymentMethodUpdate = async () => {
        const token = pb.authStore.token
        if (!token) {
            setPmError('Your session expired. Please log in again.')
            setPmModalState('error')
            return
        }

        try {
            setPmModalState('updating')
            const prep = await preparePaymentMethodUpdate(token)
            const checkoutToken = prep?.checkoutToken || prep?.data?.checkoutToken
            if (!checkoutToken) throw new Error('No checkout token returned')

            const cleanup = setupPaymentEventListener(
                checkoutToken,
                async (eventMessage: any) => {
                    removeHelcimPayIframe()
                    const last4 = (eventMessage?.cardNumber || '').slice(-4)
                    const paymentMethodType: 'cc' | 'ach' = eventMessage?.cardNumber ? 'cc' : 'ach'
                    setPmLast4(last4)
                    try {
                        await recordPaymentMethodUpdate(token, last4, paymentMethodType)
                    } catch (err) {
                        console.error('Failed to log payment method update event:', err)
                    }
                    setPmModalState('success')
                    cleanup()
                },
                async (eventMessage: any) => {
                    // ABORTED — a real failure (e.g. card declined), not a user
                    // cancellation. Must surface an error, not fail silently.
                    removeHelcimPayIframe()
                    setPmError(friendlyPaymentMethodError(eventMessage))
                    setPmModalState('error')
                    try {
                        await recordPaymentMethodFailure(
                            token,
                            typeof eventMessage === 'string' ? eventMessage : 'Unknown reason'
                        )
                    } catch (err) {
                        console.error('Failed to log payment method failure event:', err)
                    }
                    cleanup()
                },
                () => {
                    // HIDE — customer closed the Helcim form themselves. No error.
                    removeHelcimPayIframe()
                    setPmModalState('closed')
                    cleanup()
                }
            )

            appendHelcimPayIframe(checkoutToken, true, customer?.phone || '', customer?.email || '')
            // The iframe renders at z-index 998 (payment-utils.ts); our own
            // "updating" overlay was sitting above it at 999 and blocking every
            // click into the real Helcim form. Close our overlay now that the
            // iframe is in the DOM — the iframe itself is the loading UI.
            setPmModalState('closed')
        } catch (err: any) {
            console.error('Error starting payment method update:', err)
            setPmError(err?.response?.data?.error || err?.message || 'Something went wrong starting your payment method update. Please try again.')
            setPmModalState('error')
        }
    }

    return (
        <div
            className="view force-full-view"
            style={{
                padding: "8%",
                background: `linear-gradient(${page.gradient?.direction}, ${page.gradient?.first}, ${page.gradient?.second})`
            }}
        >
            {page.subtitle && <div className="center forceBlack" style={{ fontSize: '1.25rem', marginBottom: '2rem' }}>
                {page.subtitle}
            </div>}

            <div>
                {loading ? (
                    <div style={{ textAlign: 'center', color: '#232043' }}>Loading...</div>
                ) : !customer ? (
                    <div style={{ textAlign: 'center', color: '#232043', padding: '2rem' }}>
                        No customer found. Please call us at 614-699-2495 if you think this is a mistake.
                    </div>
                ) : (
                    <>
                        <h2 className="title is-4" style={{ color: '#232043', marginBottom: '1.5rem' }}>
                            Welcome back, {customer.first_name} {customer.last_name}
                        </h2>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{ marginBottom: '0.5rem', color: '#555' }}>
                                {customer.email}
                            </div>
                            <div style={{ marginBottom: '0.5rem', color: '#555' }}>
                                {customer.phone}
                            </div>
                            <div style={{ marginTop: '0.75rem' }}>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        handleLogout()
                                    }}
                                    style={{
                                        fontSize: '0.875rem',
                                        color: '#232043',
                                        textDecoration: 'underline',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Logout
                                </a>
                            </div>
                        </div>

                        {separator}

                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                style={{ background: '#232043', color: 'yellow' }}
                                onClick={() => leaseFileUrl ? window.open(leaseFileUrl, '_blank') : setShowNoLeaseModal(true)}
                            >
                                <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>
                                    {leaseFileUrl ? 'View Lease' : 'Get My Lease'}
                                </span>
                            </button>

                            <button
                                type="button"
                                style={{ background: '#232043', color: 'yellow' }}
                                onClick={() => navigate('/reserve')}
                            >
                                <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Store Another Vehicle</span>
                            </button>

                            <button
                                type="button"
                                style={{ background: '#232043', color: 'yellow' }}
                                onClick={() => { setPmConsentChecked(false); setPmModalState('consent') }}
                            >
                                <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Update Payment Method</span>
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* No lease modal */}
            {showNoLeaseModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 12, padding: '2rem',
                        maxWidth: 420, width: '90%', textAlign: 'center'
                    }}>
                        <p style={{ color: '#232043', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                            We need additional information in order to prepare your downloadable Lease.
                        </p>
                        <button
                            type="button"
                            style={{ background: '#232043', color: 'yellow', fontWeight: 700, padding: '0.6em 2em', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '1rem' }}
                            onClick={() => { setShowNoLeaseModal(false); navigate('/lease') }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Update Payment Method — consent step */}
            {pmModalState === 'consent' && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 12, padding: '2rem',
                        maxWidth: 440, width: '90%', textAlign: 'left'
                    }}>
                        <h3 style={{ color: '#232043', marginBottom: '1rem' }}>Update Payment Method</h3>
                        <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '1rem' }}>
                            You're about to add a new credit card or bank account for your storage account.
                        </p>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '1.5rem', fontSize: '0.9rem', color: '#232043', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={pmConsentChecked}
                                onChange={(e) => setPmConsentChecked(e.target.checked)}
                                style={{ marginTop: '0.2rem' }}
                            />
                            <span>
                                I understand this new payment method will become my <strong>default</strong> payment
                                method and will be used to automatically pay all future invoices on my account.
                            </span>
                        </label>
                        <div style={{ textAlign: 'center' }}>
                            <button
                                type="button"
                                disabled={!pmConsentChecked}
                                style={{
                                    background: pmConsentChecked ? '#232043' : '#999', color: 'yellow',
                                    fontWeight: 700, padding: '0.6em 2em', border: 'none', borderRadius: 4,
                                    cursor: pmConsentChecked ? 'pointer' : 'not-allowed', fontSize: '1rem'
                                }}
                                onClick={handleStartPaymentMethodUpdate}
                            >
                                Continue
                            </button>
                            <div style={{ marginTop: '0.75rem' }}>
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); setPmModalState('closed') }}
                                    style={{ fontSize: '0.875rem', color: '#232043', textDecoration: 'underline', cursor: 'pointer' }}
                                >
                                    Cancel
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Update Payment Method — waiting for Helcim iframe to open */}
            {pmModalState === 'updating' && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999
                }}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', textAlign: 'center' }}>
                        <p style={{ color: '#232043' }}>Loading secure payment form...</p>
                    </div>
                </div>
            )}

            {/* Update Payment Method — success confirmation */}
            {pmModalState === 'success' && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', maxWidth: 420, width: '90%', textAlign: 'center' }}>
                        <p style={{ color: '#232043', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                            ✅ Your new payment method{pmLast4 ? ` ending in ${pmLast4}` : ''} is now your{' '}
                            <strong>default</strong> and will be used for all future invoices on your account.
                        </p>
                        <button
                            type="button"
                            style={{ background: '#232043', color: 'yellow', fontWeight: 700, padding: '0.6em 2em', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '1rem' }}
                            onClick={() => setPmModalState('closed')}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Update Payment Method — error */}
            {pmModalState === 'error' && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', maxWidth: 420, width: '90%', textAlign: 'center' }}>
                        <p style={{ color: '#e74c3c', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                            {pmError}
                        </p>
                        <button
                            type="button"
                            style={{ background: '#232043', color: 'yellow', fontWeight: 700, padding: '0.6em 2em', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '1rem' }}
                            onClick={() => setPmModalState('closed')}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            <br />
            <br />
        </div>
    )
}

export default CustomerDashboard
